import React, { useEffect, useRef } from 'react'

interface MegaMenuProps {
  open: boolean
  onClose: () => void
  navItems: { label: string; href: string }[]
}

const SOCIALS = [
  { label: 'Instagram — Merritt & Shawn', href: 'https://www.instagram.com/merrittandshawn/' },
  { label: 'LinkedIn — Shawn McDonnell', href: 'https://www.linkedin.com/in/shawn-mcdonnell-41587513b' },
  { label: 'LinkedIn — Merritt Cooch', href: 'https://www.linkedin.com/in/merritt-cooch-2496bb125' },
]

/** Full-screen dark mega-menu opened by the header "+" (Parity V2 A.5). */
const MegaMenu: React.FC<MegaMenuProps> = ({ open, onClose, navItems }) => {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (!panel) return

    const focusable = () =>
      Array.from(panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
    focusable()[0]?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const items = focusable()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Site menu"
      className="fixed inset-0 z-[45] bg-primary-ink/95 backdrop-blur-md overflow-y-auto"
    >
      <div className="min-h-full max-w-7xl mx-auto px-6 pt-28 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-14">
        {/* Left: giant nav items, staggered fade-in */}
        <nav aria-label="Menu">
          <ul className="space-y-4">
            {navItems.map((item, i) => (
              <li
                key={item.label}
                className="megamenu-item"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <a
                  href={item.href}
                  onClick={onClose}
                  className="group font-display uppercase leading-none tracking-[0.06em] text-[clamp(34px,5vw,64px)] text-white/90 hover:text-accent-gold transition-colors duration-300"
                >
                  <span className="font-thin">{item.label.split(' ')[0]}</span>
                  {item.label.includes(' ') && (
                    <span className="font-bold"> {item.label.split(' ').slice(1).join(' ')}</span>
                  )}
                  <span className="ml-3 inline-block text-accent-gold text-[0.5em] align-middle opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
                    +
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Right: office block + socials + compliance microtext */}
        <div className="flex flex-col justify-center gap-10 lg:pl-16 lg:border-l lg:border-white/10">
          <div>
            <p className="font-display uppercase text-[12px] tracking-[0.3em] text-accent-gold mb-4">
              Office
            </p>
            <p className="font-sans text-white/80 leading-relaxed">
              1240 Kings Highway
              <br />
              Lewes, DE 19958
              <br />
              <a href="tel:+13026452207" className="hover:text-accent-gold transition-colors">
                Office 302-645-2207
              </a>
            </p>
          </div>

          <div>
            <p className="font-display uppercase text-[12px] tracking-[0.3em] text-accent-gold mb-4">
              Follow
            </p>
            <ul className="space-y-2">
              {SOCIALS.map((s) => (
                <li key={s.href}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-display uppercase text-sm tracking-[0.15em] text-white/70 hover:text-accent-gold transition-colors"
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-[10px] font-sans text-white/40 leading-relaxed">
            Equal Housing Opportunity. Shawn McDonnell, Associate Broker &amp; Merritt Cooch,
            REALTOR® · Jack Lingo REALTOR® · Licensed in Delaware.
          </p>
        </div>
      </div>
    </div>
  )
}

export default MegaMenu
